import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Search, FileWarning, Download } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export default function MaintenanceIntervals() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [data, setData] = useState(null);
  const [globalReport, setGlobalReport] = useState(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    if (user.role !== "workshop_admin" && user.role !== "client_user") {
      api.get("/reports/maintenance-intervals", { params: { only_overcharge: true, format: "json" } }).then((r) => setGlobalReport(r.data));
    }
  }, [user.role]);

  const load = async (id) => {
    if (!id) return;
    const r = await api.get(`/maintenance/intervals/${id}`);
    setData(r.data);
  };

  const onSelect = (id) => {
    setVehicleId(id);
    load(id);
  };

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        tag="Control de Mantención"
        title="Intervalos de Mantención"
        description="Analiza los días entre mantenciones de cada vehículo. Detecta sobrecargos según el contrato y genera notificaciones formales para tus clientes."
      />

      <div className="border border-gray-200 p-6 mb-6">
        <p className="veta-tag mb-3">Selecciona un vehículo</p>
        <div className="flex gap-2 flex-wrap">
          <select value={vehicleId} onChange={(e) => onSelect(e.target.value)} className="flex-1 min-w-[260px] h-12 px-3 border border-gray-300 font-mono" data-testid="maintenance-vehicle-select">
            <option value="">— Elige un vehículo —</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.brand} {v.model}</option>)}
          </select>
        </div>
      </div>

      {data && <VehicleDetail data={data} userRole={user.role} />}

      {globalReport && globalReport.count > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="veta-tag">Resumen ejecutivo</p>
              <h2 className="font-display text-2xl">Sobrecargos detectados en la flota</h2>
            </div>
            <ExportLinks basePath="/reports/maintenance-intervals" params={{ only_overcharge: true }} />
          </div>
          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{globalReport.headers.map((h, i) => <th key={i} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{h}</th>)}</tr>
              </thead>
              <tbody>
                {globalReport.rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`overcharge-row-${i}`}>
                    {r.map((c, j) => <td key={j} className="px-4 py-3 text-sm">{j === 6 && c ? `$${Number(c).toLocaleString("es-CL")}` : String(c ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-gray-600">
            Total acumulado: <span className="font-bold font-mono text-black">${(globalReport.total_overcharge || 0).toLocaleString("es-CL")} CLP</span>
          </p>
        </div>
      )}
    </div>
  );
}

function VehicleDetail({ data, userRole }) {
  const chartData = data.rows.map((r, i) => ({
    label: `#${i + 1} · ${r.date}`,
    interval: r.interval_days || 0,
    overcharge: r.overcharge_days || 0,
  })).filter((x) => x.interval > 0);

  const overchargeOrders = data.rows.filter((r) => r.overcharge_days > 0);

  return (
    <div className="space-y-6" data-testid="maintenance-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
        <Tile label="Patente" value={data.vehicle.plate} mono />
        <Tile label="Mínimo pactado" value={`${data.min_days} días`} />
        {(userRole === "rentacar_admin" || userRole === "superadmin") && (
          <Tile label="Sobrecargo / día" value={`$${(data.overcharge_per_day || 0).toLocaleString("es-CL")}`} />
        )}
        <Tile label="Total sobrecargo" value={`$${(data.total_overcharge || 0).toLocaleString("es-CL")}`} accent={data.total_overcharge > 0 ? "danger" : ""} />
      </div>

      <div className="border border-gray-200 p-6">
        <p className="veta-tag">Línea de tiempo · Intervalos vs. mínimo pactado</p>
        <h3 className="font-display text-xl mt-1 mb-4">Días entre mantenciones</h3>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-500 py-12 text-center">Aún no hay suficientes mantenciones (mínimo 2) para calcular intervalos.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="interval" fill="#0038FF" name="Días intervalo" />
              <Bar dataKey="overcharge" fill="#FF3D00" name="Días sobrecargo" />
              <ReferenceLine y={data.min_days} stroke="#FF5A00" strokeDasharray="5 5" label={{ value: `Mínimo: ${data.min_days}d`, fontSize: 10, fill: "#FF5A00" }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        <div className="p-6 border-b border-gray-200">
          <p className="veta-tag">Detalle de mantenciones</p>
          <h3 className="font-display text-xl mt-1">Historial completo · {data.rows.length} orden(es)</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>#</Th><Th>Fecha</Th><Th>Km</Th><Th>Intervalo</Th><Th>Sobrecargo</Th><Th>Monto</Th><Th>Estado</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={r.id} className={`border-b border-gray-200 ${r.overcharge_days > 0 ? "bg-red-50/40" : ""}`} data-testid={`maintenance-row-${i}`}>
                <td className="px-4 py-3 text-sm font-mono">{i + 1}</td>
                <td className="px-4 py-3 text-sm font-mono">{r.date}</td>
                <td className="px-4 py-3 text-sm font-mono">{(r.mileage || 0).toLocaleString("es-CL")}</td>
                <td className="px-4 py-3 text-sm font-bold">{r.interval_days != null ? `${r.interval_days} d` : "—"}</td>
                <td className="px-4 py-3 text-sm">
                  {r.overcharge_days > 0 ? <span className="status-danger px-2 py-0.5 text-xs">{r.overcharge_days} d</span> : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-sm font-mono">{r.overcharge_amount > 0 ? `$${Number(r.overcharge_amount).toLocaleString("es-CL")}` : "—"}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider">{r.status}</td>
                <td className="px-4 py-3 text-right">
                  {r.overcharge_days > 0 && (userRole === "rentacar_admin" || userRole === "superadmin") && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/pdf/overcharge-notice/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-bold uppercase tracking-wider text-blue-600 underline inline-flex items-center gap-1"
                      data-testid={`overcharge-pdf-${i}`}
                    >
                      <Download size={12} strokeWidth={2.5} /> Notificación
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, mono, accent }) {
  const bg = accent === "danger" ? "bg-red-50" : "bg-white";
  return (
    <div className={`${bg} p-5`}>
      <p className="veta-tag">{label}</p>
      <p className={`font-display text-2xl mt-1 ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function ExportLinks({ basePath, params }) {
  const open = (fmt) => {
    const qs = new URLSearchParams({ ...params, format: fmt }).toString();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api${basePath}?${qs}`, "_blank");
  };
  return (
    <div className="flex gap-2">
      <button onClick={() => open("csv")} className="h-10 px-3 border-2 border-black text-xs font-bold uppercase">CSV</button>
      <button onClick={() => open("xlsx")} className="h-10 px-3 border-2 border-green-700 text-green-700 text-xs font-bold uppercase">Excel</button>
      <button onClick={() => open("pdf")} className="h-10 px-3 border-2 border-blue-600 text-blue-600 text-xs font-bold uppercase">PDF</button>
    </div>
  );
}
